import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useBrandReview } from "../hooks/useBrandReview";
import { OperatorBrandGate } from "../components/layout/OperatorBrandGate";
import { AgentUIComposer } from "../components/agent-v2/AgentUIComposer";
import { WorkspaceContentFrame } from "../components/workspace/WorkspaceContentFrame";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { buildBrandReviewPath, buildBrandScopedPath } from "../lib/brand-navigation";
import { timeAgo } from "../lib/utils";
import { taskRunJobLabel } from "../lib/task-run-presentation";
import {
  isBlockingReviewAlert,
  reviewTrustExplanation,
  reviewTrustOperatorActionText,
} from "../../../shared/review-source-risk";
import type { Brief, ReviewApprovalItem, ReviewRecentRun, ReviewTrustAlert } from "../../../shared/types";

function workflowHref(brandId: string, brief: Brief | ReviewApprovalItem): string {
  return `${buildBrandReviewPath(brandId, `briefId=${encodeURIComponent(brief.id)}`)}#brief-${encodeURIComponent(brief.id)}`;
}

function workflowLabel(brief: Brief | ReviewApprovalItem): string {
  return brief.surface ? `${brief.title} · ${brief.surface}` : brief.title;
}

export function WorkflowsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeBrand, activeBrandId: brandId } = useActiveBrand();
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const { data: reviewRes, isLoading, error } = useBrandReview(brandId);
  const review = reviewRes?.data ?? null;

  useEffect(() => {
    document.title = "Operations — HelloConvo";
  }, []);

  if (!brandId) {
    return (
      <OperatorBrandGate
        title="Choose a brand before opening operations"
        description="Operations are brand-scoped. Select the brand you intend to check for Runs that need attention."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error instanceof Error ? error.message : "Failed to load operations"}
      </div>
    );
  }

  const runningNow = review.recent_runs.filter((run) => run.status === "running");
  const completedRecently = review.recent_runs.filter((run) => run.status === "completed");
  const waitingOnYou = review.awaiting_approval;
  const blocked = review.stuck_briefs;
  const trustAlerts = review.trust_alerts;
  const blockingTrustAlerts = trustAlerts.filter(isBlockingReviewAlert);
  const nonBlockingTrustAlerts = trustAlerts.filter((alert) => !isBlockingReviewAlert(alert));
  const blockerCount = blocked.length + blockingTrustAlerts.length;
  const returnToRunId = new URLSearchParams(location.search).get("return_to_run");
  const returnToRunHref = returnToRunId && /^[A-Za-z0-9-]{1,100}$/.test(returnToRunId)
    ? buildBrandScopedPath(`/runs/${encodeURIComponent(returnToRunId)}`, brandId)
    : null;
  const operationsSearch = new URLSearchParams({ brandId });
  if (returnToRunHref && returnToRunId) operationsSearch.set("return_to_run", returnToRunId);

  return (
    <WorkspaceContentFrame
      mobileInspectorLabel="Run health"
      mobileSecondaryLabel="Recent Runs"
      stickyInspector
      stickySecondary
      inspectorOpen={inspectorOpen}
      inspector={
        <aside className="hidden w-[320px] shrink-0 border-l border-slate-200 bg-slate-50/70 xl:flex xl:flex-col">
          <div className="border-b border-slate-200 px-4 py-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Run health</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">What needs attention</h3>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Recent Runs</p>
                <div className="mt-3 space-y-3">
                  {review.recent_runs.slice(0, 5).map((run: ReviewRecentRun) => (
                    <div key={run.id}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-slate-900">{taskRunJobLabel(run)}</span>
                        <span className="font-mono text-[10px] text-slate-400">{timeAgo(run.started_at)}</span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">Status: {run.status}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </aside>
      }
    >
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-6 py-8">
        <header className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Operations</p>
            <h1 className="mt-1 text-[32px] font-bold tracking-tight text-slate-900">
              Operations{activeBrand?.name ? ` · ${activeBrand.name}` : ""}
            </h1>
            <p className="mt-2 max-w-2xl text-[15px] leading-7 text-slate-500">
              Use this optional view to check Runs that need operational follow-up. Open individual Results from Runs history.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {returnToRunHref ? (
              <Link to={returnToRunHref} className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Return to Result
              </Link>
            ) : null}
            <Button variant="secondary" onClick={() => setInspectorOpen((open) => !open)}>
              {inspectorOpen ? "Hide Run health" : "Show Run health"}
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/workflows?${operationsSearch.toString()}`)}>Refresh status</Button>
          </div>
        </header>

        <div className="mt-8 grid gap-4 md:grid-cols-4" aria-label="Operations snapshot">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Review handoffs</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{waitingOnYou.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Running</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{runningNow.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Completed recently</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{completedRecently.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Blocked signals</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{blockerCount}</p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 xl:grid-cols-2">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Review handoffs</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">Content items live in Review</h2>
              </div>
            </div>
            <div className="space-y-3">
              {waitingOnYou.length > 0 ? waitingOnYou.map((brief) => (
                <div key={brief.id} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4">
                  <div className="min-w-0">
                    <div className="mb-1 flex items-center gap-2">
                      <Badge variant="warning">{brief.state.replace(/_/g, " ")}</Badge>
                      <span className="truncate text-xs text-slate-400">{formatDate(brief.due_at)}</span>
                    </div>
                    <p className="truncate text-sm font-medium text-slate-900">{workflowLabel(brief)}</p>
                  </div>
                  <Link
                    to={workflowHref(brandId, brief)}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open in Review
                  </Link>
                </div>
              )) : (
                <p className="text-sm text-slate-500">Nothing is waiting on you right now.</p>
              )}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Running now</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">Active execution</h2>
              </div>
            </div>
            <div className="space-y-3">
              {runningNow.length > 0 ? runningNow.map((run) => (
                <Link
                  key={run.id}
                  to={buildBrandScopedPath(`/runs/${encodeURIComponent(run.id)}`, brandId)}
                  className="block rounded-2xl border border-slate-200 bg-white px-4 py-4 transition hover:border-brand-200 hover:bg-brand-50/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-900">{taskRunJobLabel(run)}</span>
                    <span className="font-mono text-[10px] text-slate-400">{timeAgo(run.started_at)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">Run in progress</p>
                </Link>
              )) : (
                <p className="text-sm text-slate-500">No active runs right now.</p>
              )}
            </div>
          </section>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Completed</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">Recently finished work</h2>
              </div>
            </div>
            <div className="space-y-3">
              {completedRecently.length > 0 ? completedRecently.map((run) => (
                <Link
                  key={run.id}
                  to={buildBrandScopedPath(`/runs/${encodeURIComponent(run.id)}`, brandId)}
                  className="block rounded-2xl border border-slate-200 bg-white px-4 py-4 transition hover:border-brand-200 hover:bg-brand-50/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-900">{taskRunJobLabel(run)}</span>
                    <Badge variant="success">Completed</Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">Open Result</p>
                </Link>
              )) : (
                <p className="text-sm text-slate-500">No completed runs in this recent window.</p>
              )}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Blocked</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">Things stopping progress</h2>
              </div>
            </div>
            <div className="space-y-3">
              {blocked.map((brief) => (
                <div key={brief.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-900">{brief.title}</span>
                    <Badge variant="error">Blocked</Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{brief.blocked_reason || "Needs operator intervention."}</p>
                  <Link
                    to={workflowHref(brandId, brief)}
                    className="mt-3 inline-flex items-center rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-100"
                  >
                    Open in Review
                  </Link>
                </div>
              ))}
              {blockingTrustAlerts.map((alert: ReviewTrustAlert) => (
                <div key={alert.id} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-900">{alert.title}</span>
                    <Badge variant="error">blocker</Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{reviewTrustExplanation(alert)}</p>
                  <p className="mt-2 text-xs text-slate-500">{reviewTrustOperatorActionText(alert)}</p>
                  {alert.run_id ? (
                    <Link
                      to={buildBrandScopedPath(`/runs/${encodeURIComponent(alert.run_id)}`, brandId)}
                      className="mt-3 inline-flex items-center rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                    >
                      Inspect run evidence
                    </Link>
                  ) : null}
                </div>
              ))}
              {blocked.length === 0 && blockingTrustAlerts.length === 0 ? (
                <p className="text-sm text-slate-500">No blockers right now.</p>
              ) : null}
            </div>
          </section>
        </div>

        {nonBlockingTrustAlerts.length > 0 ? (
          <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Trust context</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">Non-blocking trust notes</h2>
              </div>
              <Badge variant="info">{nonBlockingTrustAlerts.length}</Badge>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {nonBlockingTrustAlerts.map((alert: ReviewTrustAlert) => (
                <div key={alert.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-900">{alert.title}</span>
                    <Badge variant={alert.severity === "info" ? "info" : "warning"}>{alert.severity ?? "warning"}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{reviewTrustExplanation(alert)}</p>
                  <p className="mt-2 text-xs text-slate-500">{reviewTrustOperatorActionText(alert)}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <div className="border-t border-transparent pt-4">
          <AgentUIComposer
            onSend={(content) => navigate(`/agent/${brandId}`, { state: { draft: content } })}
            onStop={() => {}}
            isStreaming={false}
            disabled={!brandId}
            initialValue=""
            contextLabel="Ask HelloConvo to summarize blockers or queue priority"
          />
        </div>
      </div>
    </WorkspaceContentFrame>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "Unscheduled";
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
